import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import UpcomingAppointmentsCard from '../components/UpcomingAppointmentsCard';
import { formatDateTime, formatRelativeTime } from './inquiriesShared';
import './InquiryTasksPage.css';

const getUpcomingRange = () => {
  const from = new Date();
  const to = new Date(from);
  to.setDate(to.getDate() + 30);
  return { from: from.toISOString(), to: to.toISOString() };
};

const getAssigneeLabel = (task) => (
  task?.assigned_member?.display_name
  || task?.assigned_member?.email
  || task?.task_assigned_member?.display_name
  || task?.task_assigned_member?.email
  || task?.inquiry_assigned_member?.display_name
  || task?.inquiry_assigned_member?.email
  || 'Ufordelt'
);

const getTaskTimingLabel = (task) => {
  if (task?.scheduled_at) return `Planlagt ${formatDateTime(task.scheduled_at)}`;
  if (task?.planned_start_at || task?.planned_end_at) {
    return `Planlagt ${formatDateTime(task.planned_start_at || task.planned_end_at)}`;
  }
  return 'Ikke planlagt';
};

const getTaskPriorityValue = (task) => {
  const priority = Number(task?.inquiry_priority ?? task?.inquiryPriority ?? 0);
  return Number.isFinite(priority) ? Math.max(0, Math.min(100, priority)) : 0;
};

const getTaskSortTime = (task) => {
  const value = task?.scheduled_at
    || task?.inquiry?.last_activity_at
    || task?.inquiry?.submitted_at
    || task?.inquiry?.created_at
    || null;
  const timestamp = value ? Date.parse(value) : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const compareInquiryTasks = (left, right) => (
  getTaskPriorityValue(right) - getTaskPriorityValue(left)
  || getTaskSortTime(right) - getTaskSortTime(left)
  || Number(right?.progress_percent || 0) - Number(left?.progress_percent || 0)
  || Number(left?.step_sort_order || 0) - Number(right?.step_sort_order || 0)
  || Number(left?.task_sort_order || 0) - Number(right?.task_sort_order || 0)
  || Number(right?.inbox_id || 0) - Number(left?.inbox_id || 0)
);

function InquiryTasksPage() {
  const { selectedCompanyId, permissions } = useAuth();
  const companyId = selectedCompanyId;
  const [tasks, setTasks] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId || !permissions?.canViewInbox) {
      setTasks([]);
      setUpcomingEvents([]);
      setLoading(false);
      return undefined;
    }

    let isMounted = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const range = getUpcomingRange();
        const [tasksResult, eventsResult] = await Promise.all([
          DashboardApi.getCompanyInboxTasks(companyId, { limit: 75 }),
          DashboardApi.getCompanyInboxCalendar(companyId, {
            from: range.from,
            to: range.to,
            assigned: 'mine_or_unassigned',
            limit: 5,
          }),
        ]);
        if (!isMounted) return;
        setTasks(Array.isArray(tasksResult?.tasks) ? tasksResult.tasks : []);
        setUpcomingEvents(Array.isArray(eventsResult?.events) ? eventsResult.events : []);
      } catch (err) {
        if (!isMounted) return;
        setTasks([]);
        setUpcomingEvents([]);
        setError(err.message || 'Kunne ikke hente oppgaver.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [companyId, permissions?.canViewInbox]);

  const visibleTasks = useMemo(
    () => (Array.isArray(tasks) ? [...tasks].sort(compareInquiryTasks) : []),
    [tasks],
  );

  if (!permissions?.canViewInbox) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page inquiry-tasks-page">
      <div className="page-header">
        <div>
          <h1>Oppgaver</h1>
          <p className="page-subtitle">Manglende arbeid på henvendelser, prioritert etter henvendelsesprioritet.</p>
        </div>
        <Link to="/henvendelser/innboks" className="button button-secondary">
          Åpne innboks
        </Link>
      </div>

      {error && (
        <div className="card inquiry-tasks-error">
          {error}
        </div>
      )}

      <UpcomingAppointmentsCard events={upcomingEvents} loading={loading} />

      <section className="card inquiry-tasks-card">
        <header className="card-header inquiry-tasks-header">
          <div>
            <span className="card-title">Neste arbeid</span>
            <p className="inquiry-tasks-subtitle">Fremtidig planlagte steg er skjult fra listen.</p>
          </div>
          <span className="inquiry-tasks-count">{loading ? '...' : `${visibleTasks.length} oppgaver`}</span>
        </header>

        {loading ? (
          <div className="inquiry-tasks-empty">Laster oppgaver...</div>
        ) : visibleTasks.length === 0 ? (
          <div className="inquiry-tasks-empty">Ingen oppgaver som må startes nå.</div>
        ) : (
          <div className="inquiry-tasks-list">
            {visibleTasks.map((task) => {
              const activityAt = task?.inquiry?.last_activity_at || task?.inquiry?.submitted_at || task?.inquiry?.created_at;
              const inquiryTitle = task?.inquiry?.display_key || task?.inquiry?.display_name || `Henvendelse #${task.inbox_id}`;
              const productName = task?.product?.name || 'Uten produkt';
              const progress = Number.isFinite(Number(task?.progress_percent)) ? Number(task.progress_percent) : 0;
              return (
                <Link
                  key={task.id || `${task.inbox_id}-${task.step_key}`}
                  to={`/henvendelser/${task.inbox_id}`}
                  className="inquiry-tasks-row"
                >
                  <span className="inquiry-tasks-step">
                    <strong>{task.step_label || task.step_key}</strong>
                    <small>{task.status_label || task.status || 'Ikke startet'}</small>
                  </span>
                  <span className="inquiry-tasks-inquiry">
                    <strong>{inquiryTitle}</strong>
                    <small>{productName}</small>
                  </span>
                  <span className="inquiry-tasks-progress">
                    <span className="inquiry-tasks-progress-bar" aria-hidden="true">
                      <span style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
                    </span>
                    <small>{progress}% · {task.progression_status_label || 'Pågår'}</small>
                  </span>
                  <span className="inquiry-tasks-meta">
                    <small>{getAssigneeLabel(task)}</small>
                    <small>{getTaskTimingLabel(task)}</small>
                    {activityAt ? <small>{formatRelativeTime(activityAt)}</small> : null}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default InquiryTasksPage;
